import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import {
  Box,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  IconButton,
  InputBase,
  Menu,
  MenuItem,
  Button,
  Typography,
  Grid,
  Tooltip,
  CircularProgress,
  FormControl,
  Select,
  InputLabel,
  TextField
} from '@mui/material';
import SearchIcon from '@mui/icons-material/Search';
import FileDownloadIcon from '@mui/icons-material/FileDownload';
import AddIcon from '@mui/icons-material/Add';
import RemoveIcon from '@mui/icons-material/Remove';
import * as XLSX from 'xlsx';
import { useDispatch } from 'react-redux';
import { storeDataComponent } from '../../StateManagement/actions';
import http from '../../commonFunctions/httpClient';

export interface DataGridProps {
  id: string;
  columns: IColumns[];
  data?: Array<any>;
  source?: {
    url: string;
    pageSize: number;
    page: number;
    filter: any[];
    sort: any[];
    type?: 'POST' | 'GET';
  };
  transFormData?: (data: any, totalCount?: any) => any;
}

export interface IColumns {
  header: string;
  fieldName: string;
  isHide?: boolean;
}

export const WSDadaGridPremium: React.FC<DataGridProps> = ({
  id,
  columns,
  data = [],
  source,
  transFormData,
}) => {
  const [rows, setRows] = useState<any[]>([]);
  const [totalCount, setTotalCount] = useState<number>(0);
  const [page, setPage] = useState<number>(0);
  const [rowsPerPage, setRowsPerPage] = useState<number>(source?.pageSize || 10);
  const [loading, setLoading] = useState<boolean>(false);
  const [filter, setFilter] = useState<string>('');
  const [searchOpen, setSearchOpen] = useState(false);
  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
  const [dynamicFilters, setDynamicFilters] = useState<any[]>([{ field: '', condition: 'contains', value: '' }]);
  const dispatch = useDispatch();
  const hasFetchedRef = useRef(false);

  const fetchDataFromAPI = async (
    page: number,
    pageSize: number,
    filter: any[] = [],
    sort: any[] = []
  ) => {
    try {
      setLoading(true);
      if (source?.type === 'GET') {
        const response = await http.get(source.url);
        return response;
      } else if (source) {
        const response = await http.post(source.url, {
          page,
          pageSize,
          filter,
          sort: sort.length > 0 ? sort : source?.sort || [],
        });
        return response;
      }
    } catch (error) {
      console.error('Error fetching data:', error);
      return null;
    } finally {
      setLoading(false);
    }
  };

  const loadPageData = useCallback(async () => {
    if (!source) return;
    try {
      const response: any = await fetchDataFromAPI(
        page + 1,
        rowsPerPage,
        source.filter || [],
        source.sort || []
      );

      const responseData = response?.data || [];
      const total = response?.pagination?.totalCount || 0;

      dispatch(storeDataComponent(id, responseData));
      setTotalCount(total);
      setRows(transFormData ? transFormData(responseData, total) : responseData);
    } catch (error) {
      console.error('Error loading page data:', error);
    }
  }, [columns, dispatch, id, page, rowsPerPage, transFormData]);

  useEffect(() => {
    if (data.length > 0) {
      const startIndex = page * rowsPerPage;
      const paginatedData = data.slice(startIndex, startIndex + rowsPerPage);
      setRows(paginatedData);
      setTotalCount(data.length);
      dispatch(storeDataComponent(id, data));
    } else if (!hasFetchedRef.current) {
      hasFetchedRef.current = true;
      loadPageData();
    }
  }, [data, page, rowsPerPage]);

  const filteredRows = rows.filter(row => {
    const baseSearch = Object.values(row).some(value =>
      value?.toString().toLowerCase().includes(filter.toLowerCase())
    );
    return baseSearch && dynamicFilters.every(df => {
      if (!df.field || df.value === '') return true;
      const fieldValue = String(row[df.field] || '').toLowerCase();
      const compareValue = String(df.value).toLowerCase();
      switch (df.condition) {
        case 'contains':
          return fieldValue.includes(compareValue);
        case 'equals':
          return fieldValue === compareValue;
        case 'startsWith':
          return fieldValue.startsWith(compareValue);
        case 'endsWith':
          return fieldValue.endsWith(compareValue);
        default:
          return true;
      }
    });
  });

  const handleExportExcel = () => {
    const worksheet = XLSX.utils.json_to_sheet(filteredRows);
    const workbook = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(workbook, worksheet, 'Sheet1');
    XLSX.writeFile(workbook, 'data.xlsx');
    setAnchorEl(null);
  };

  const updateDynamicFilter = (index: number, field: string, value: any) => {
    const newFilters = [...dynamicFilters];
    newFilters[index][field] = value;
    setDynamicFilters(newFilters);
  };

  return (
    <Box p={2}>
      <Grid container justifyContent="space-between" alignItems="center" mb={2}>
        <Grid item>
          <Typography variant="h6">Grid</Typography>
        </Grid>
        <Grid item display="flex" gap={1} alignItems="center">
          {searchOpen && (
            <InputBase
              placeholder="Search…"
              value={filter}
              onChange={(e) => setFilter(e.target.value)}
              autoFocus
              sx={{ px: 1, border: '1px solid #ccc', borderRadius: 2 }}
            />
          )}
          <Tooltip title="Search">
            <IconButton onClick={() => setSearchOpen(prev => !prev)}>
              <SearchIcon />
            </IconButton>
          </Tooltip>
          <Tooltip title="Export">
            <IconButton onClick={(e) => setAnchorEl(e.currentTarget)}>
              <FileDownloadIcon />
            </IconButton>
          </Tooltip>
          <Menu
            anchorEl={anchorEl}
            open={Boolean(anchorEl)}
            onClose={() => setAnchorEl(null)}
          >
            <MenuItem onClick={handleExportExcel}>Export to Excel</MenuItem>
          </Menu>
        </Grid>
      </Grid>

      {/* Dynamic Filters */}
      <Box mb={2}>
        {dynamicFilters.map((filter, index) => (
          <Grid container spacing={1} alignItems="center" key={index} mb={1}>
            <Grid item xs={3}>
              <FormControl fullWidth size="small">
                <InputLabel>Field</InputLabel>
                <Select
                  value={filter.field}
                  label="Field"
                  onChange={(e) => updateDynamicFilter(index, 'field', e.target.value)}
                >
                  {columns.map(col => (
                    <MenuItem key={col.fieldName} value={col.fieldName}>
                      {col.header}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>
            <Grid item xs={3}>
              <FormControl fullWidth size="small">
                <InputLabel>Condition</InputLabel>
                <Select
                  value={filter.condition}
                  label="Condition"
                  onChange={(e) => updateDynamicFilter(index, 'condition', e.target.value)}
                >
                  <MenuItem value="contains">contains</MenuItem>
                  <MenuItem value="equals">equals</MenuItem>
                  <MenuItem value="startsWith">starts with</MenuItem>
                  <MenuItem value="endsWith">ends with</MenuItem>
                </Select>
              </FormControl>
            </Grid>
            <Grid item xs={4}>
              <TextField
                fullWidth
                size="small"
                label="Value"
                value={filter.value}
                onChange={(e) => updateDynamicFilter(index, 'value', e.target.value)}
              />
            </Grid>
            <Grid item xs={2} display="flex" justifyContent="center">
              <IconButton onClick={() => setDynamicFilters(filters => filters.filter((_, i) => i !== index))}>
                <RemoveIcon />
              </IconButton>
              {index === dynamicFilters.length - 1 && (
                <IconButton onClick={() => setDynamicFilters([...dynamicFilters, { field: '', condition: 'contains', value: '' }])}>
                  <AddIcon />
                </IconButton>
              )}
            </Grid>
          </Grid>
        ))}
      </Box>

      <Paper>
        <TableContainer>
          <Table>
            <TableHead>
              <TableRow>
                {columns.filter(col => !col.isHide).map(col => (
                  <TableCell key={col.fieldName}>{col.header}</TableCell>
                ))}
              </TableRow>
            </TableHead>
            <TableBody>
              {loading ? (
                <TableRow>
                  <TableCell colSpan={columns.length} align="center">
                    <CircularProgress />
                  </TableCell>
                </TableRow>
              ) : (
                filteredRows.map(row => (
                  <TableRow key={row.id}>
                    {columns.filter(col => !col.isHide).map(col => (
                      <TableCell key={col.fieldName}>{row[col.fieldName]}</TableCell>
                    ))}
                  </TableRow>
                ))
              )}
            </TableBody>
          </Table>
        </TableContainer>
      </Paper>
    </Box>
  );
};
